/**
 * MasterClassFormModal - Модальное окно для создания/редактирования мастер-класса
 *
 * Особенности:
 * - Динамические группы (добавление/удаление)
 * - Выбор предмета, количества детей, длительности, педагога для каждой группы
 * - Выбор менеджера и сейла
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Textarea,
  Select,
  Button,
  Group,
  Stack,
  Alert,
  Divider,
  Box,
  Text,
  ActionIcon,
  NumberInput,
  Paper,
  Title,
} from '@mantine/core';
import { DatePickerInput, TimeInput } from '@mantine/dates';
import {
  IconAlertCircle,
  IconCalendar,
  IconClock,
  IconPlus,
  IconTrash,
  IconUsers,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import tokens from '../../../theme/designTokens';

/**
 * Форматирование имени пользователя
 */
const formatUserName = (user) => {
  if (!user) return '';
  const parts = [];
  if (user.last_name) parts.push(user.last_name);
  if (user.first_name) parts.push(user.first_name);
  return parts.join(' ') || user.email;
};

/**
 * Преобразование минут в строку HH:MM
 */
const minutesToTimeString = (minutes) => {
  if (!minutes && minutes !== 0) return '';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

/**
 * Преобразование строки HH:MM в минуты
 */
const timeStringToMinutes = (timeString) => {
  if (!timeString) return null;
  const [hours, mins] = timeString.split(':').map(Number);
  return (hours || 0) * 60 + (mins || 0);
};

/**
 * Опции для выбора длительности (от 15 минут до 4 часов с шагом 15 минут)
 */
const DURATION_OPTIONS = [
  { value: '15', label: '15 мин' },
  { value: '30', label: '30 мин' },
  { value: '45', label: '45 мин' },
  { value: '60', label: '1 час' },
  { value: '75', label: '1 ч 15 мин' },
  { value: '90', label: '1 ч 30 мин' },
  { value: '105', label: '1 ч 45 мин' },
  { value: '120', label: '2 часа' },
  { value: '135', label: '2 ч 15 мин' },
  { value: '150', label: '2 ч 30 мин' },
  { value: '165', label: '2 ч 45 мин' },
  { value: '180', label: '3 часа' },
  { value: '210', label: '3 ч 30 мин' },
  { value: '240', label: '4 часа' },
];

/**
 * Компонент одной группы
 */
const GroupItem = ({
  group,
  index,
  subjects,
  teachers,
  onChange,
  onRemove,
  canRemove,
}) => {
  return (
    <Paper
      p="md"
      withBorder
      radius="xl"
      style={{ backgroundColor: tokens.colors.neutral[1] }}
    >
      <Group justify="space-between" mb="sm">
        <Text fw={500} size="sm">
          {group.name}
        </Text>
        {canRemove && (
          <ActionIcon
            variant="subtle"
            color="red"
            onClick={() => onRemove(index)}
            title="Удалить группу"
          >
            <IconTrash size={16} />
          </ActionIcon>
        )}
      </Group>

      <Stack gap="sm">
        <Group grow>
          {/* Предмет */}
          <Select
            label="Предмет"
            placeholder="Выберите предмет"
            required
            radius="xl"
            data={subjects.map(s => ({
              value: s.id.toString(),
              label: s.name,
            }))}
            value={group.subject_id?.toString() || null}
            onChange={(value) => onChange(index, 'subject_id', value ? parseInt(value, 10) : null)}
            searchable
            clearable
          />

          {/* Количество детей */}
          <NumberInput
            label="Количество детей"
            min={0}
            radius="xl"
            value={group.children_count ?? 0}
            onChange={(value) => onChange(index, 'children_count', value === '' ? 0 : (value ?? 0))}
            leftSection={<IconUsers size={14} />}
            allowNegative={false}
            allowDecimal={false}
          />
        </Group>

        <Group grow>
          {/* Длительность */}
          <Select
            label="Длительность"
            placeholder="Выберите"
            radius="xl"
            data={DURATION_OPTIONS}
            value={group.duration_minutes?.toString() || null}
            onChange={(value) => onChange(index, 'duration_minutes', value ? parseInt(value, 10) : null)}
            leftSection={<IconClock size={14} />}
            searchable
            clearable
          />

          {/* Педагог */}
          <Select
            label="Педагог"
            placeholder="Выберите педагога"
            radius="xl"
            data={teachers.map(t => ({
              value: t.id.toString(),
              label: formatUserName(t),
            }))}
            value={group.teacher_user_id?.toString() || null}
            onChange={(value) => onChange(index, 'teacher_user_id', value ? parseInt(value, 10) : null)}
            searchable
            clearable
          />
        </Group>
      </Stack>
    </Paper>
  );
};

const MasterClassFormModal = ({
  opened,
  onClose,
  onSubmit,
  masterClass = null,
  organization,
  subjects = [],
  users = [],
  loading = false,
}) => {
  const isEditing = !!masterClass;

  // Фильтруем пользователей по ролям
  const teachers = users.filter(u => u.role === 'TEACHER');
  const managers = users.filter(u => ['ADMIN', 'MANAGER'].includes(u.role));
  const salesUsers = users.filter(u => ['ADMIN', 'MANAGER', 'SALES'].includes(u.role));

  const [formData, setFormData] = useState({
    scheduled_date: new Date(),
    scheduled_time: '',
    manager_user_id: null,
    sales_user_id: null,
    comment: '',
    groups: [
      { name: 'Группа 1', subject_id: null, children_count: 8, duration_minutes: null, teacher_user_id: null },
    ],
  });
  const [error, setError] = useState(null);

  // Форматирование времени из даты
  const formatTimeFromDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Инициализация формы при открытии
  useEffect(() => {
    if (opened) {
      if (masterClass) {
        // Редактирование
        const scheduledDate = new Date(masterClass.scheduled_at);
        setFormData({
          scheduled_date: scheduledDate,
          scheduled_time: formatTimeFromDate(scheduledDate),
          manager_user_id: masterClass.manager_user_id,
          sales_user_id: masterClass.sales_user_id,
          comment: masterClass.comment || '',
          groups: masterClass.groups.map((g, i) => ({
            name: g.name || `Группа ${i + 1}`,
            subject_id: g.subject_id,
            children_count: g.children_count ?? 0,
            duration_minutes: g.duration_minutes,
            teacher_user_id: g.teacher_user_id,
          })),
        });
      } else {
        // Создание - инициализируем значениями по умолчанию
        const now = new Date();
        // Устанавливаем время на следующий час
        now.setHours(now.getHours() + 1, 0, 0, 0);

        setFormData({
          scheduled_date: now,
          scheduled_time: formatTimeFromDate(now),
          manager_user_id: organization?.manager_user_id || null,
          sales_user_id: organization?.assigned_to_user_id || null,
          comment: '',
          groups: [
            { name: 'Группа 1', subject_id: null, children_count: 8, duration_minutes: null, teacher_user_id: null },
          ],
        });
      }
      setError(null);
    }
  }, [opened, masterClass, organization]);

  // Добавить группу
  const addGroup = () => {
    const newGroupName = `Группа ${formData.groups.length + 1}`;
    setFormData({
      ...formData,
      groups: [
        ...formData.groups,
        { name: newGroupName, subject_id: null, children_count: 8, duration_minutes: null, teacher_user_id: null },
      ],
    });
  };

  // Удалить группу
  const removeGroup = (index) => {
    if (formData.groups.length === 1) {
      notifications.show({
        title: 'Невозможно удалить',
        message: 'Нельзя удалить единственную группу. Отмените мастер-класс целиком.',
        color: 'red',
      });
      return;
    }

    const newGroups = formData.groups.filter((_, i) => i !== index);
    // Переименовываем группы
    const renamedGroups = newGroups.map((g, i) => ({
      ...g,
      name: `Группа ${i + 1}`,
    }));

    setFormData({
      ...formData,
      groups: renamedGroups,
    });
  };

  // Изменить поле группы
  const updateGroup = (index, field, value) => {
    const newGroups = [...formData.groups];
    newGroups[index] = {
      ...newGroups[index],
      [field]: value,
    };
    setFormData({
      ...formData,
      groups: newGroups,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Валидация
    if (!formData.scheduled_date) {
      setError('Укажите дату мастер-класса');
      return;
    }

    if (!formData.scheduled_time) {
      setError('Укажите время мастер-класса');
      return;
    }

    if (formData.groups.length === 0) {
      setError('Добавьте хотя бы одну группу');
      return;
    }

    // Проверяем, что у каждой группы указан предмет
    for (let i = 0; i < formData.groups.length; i++) {
      if (!formData.groups[i].subject_id) {
        setError(`Укажите предмет для группы ${i + 1}`);
        return;
      }
    }

    try {
      // Собираем дату и время в один datetime
      const [hours, minutes] = formData.scheduled_time.split(':').map(Number);
      const scheduledAt = new Date(formData.scheduled_date);
      scheduledAt.setHours(hours || 0, minutes || 0, 0, 0);

      const data = {
        scheduled_at: scheduledAt.toISOString(),
        manager_user_id: formData.manager_user_id,
        sales_user_id: formData.sales_user_id,
        comment: formData.comment?.trim() || null,
        groups: formData.groups.map(g => ({
          name: g.name,
          subject_id: g.subject_id,
          children_count: g.children_count,
          duration_minutes: g.duration_minutes,
          teacher_user_id: g.teacher_user_id,
        })),
      };

      await onSubmit(data);
    } catch (err) {
      setError(err.message || 'Ошибка при сохранении мастер-класса');
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEditing ? 'Редактировать мастер-класс' : 'Новый мастер-класс'}
      size="xl"
      radius="xl"
      styles={{
        content: {
          borderRadius: '16px',
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {error}
            </Alert>
          )}

          {/* Дата и время */}
          <Group grow>
            <DatePickerInput
              label="Дата проведения"
              placeholder="Выберите дату"
              required
              radius="xl"
              value={formData.scheduled_date}
              onChange={(value) => setFormData({ ...formData, scheduled_date: value })}
              leftSection={<IconCalendar size={16} />}
              valueFormat="DD.MM.YYYY"
              locale="ru"
            />
            <TimeInput
              label="Время"
              placeholder="14:00"
              required
              radius="xl"
              value={formData.scheduled_time}
              onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
              leftSection={<IconClock size={16} />}
            />
          </Group>

          <Divider label="Группы" labelPosition="left" />

          {/* Список групп */}
          <Stack gap="sm">
            {formData.groups.map((group, index) => (
              <GroupItem
                key={index}
                group={group}
                index={index}
                subjects={subjects.filter(s => s.is_active)}
                teachers={teachers}
                onChange={updateGroup}
                onRemove={removeGroup}
                canRemove={formData.groups.length > 1}
              />
            ))}
          </Stack>

          {/* Кнопка добавления группы */}
          <Button
            variant="light"
            radius="xl"
            leftSection={<IconPlus size={14} />}
            onClick={addGroup}
            fullWidth
          >
            Добавить группу
          </Button>

          <Divider label="Ответственные" labelPosition="left" />

          {/* Менеджер и Сейл */}
          <Group grow>
            <Select
              label="Менеджер"
              placeholder="Выберите менеджера"
              radius="xl"
              data={managers.map(m => ({
                value: m.id.toString(),
                label: formatUserName(m),
              }))}
              value={formData.manager_user_id?.toString() || null}
              onChange={(value) => setFormData({
                ...formData,
                manager_user_id: value ? parseInt(value, 10) : null,
              })}
              searchable
              clearable
            />
            <Select
              label="Сейл"
              placeholder="Выберите сейла"
              radius="xl"
              data={salesUsers.map(s => ({
                value: s.id.toString(),
                label: formatUserName(s),
              }))}
              value={formData.sales_user_id?.toString() || null}
              onChange={(value) => setFormData({
                ...formData,
                sales_user_id: value ? parseInt(value, 10) : null,
              })}
              searchable
              clearable
            />
          </Group>

          {/* Комментарий */}
          <Textarea
            label="Комментарий"
            placeholder="Заметки о мастер-классе..."
            radius="xl"
            autosize
            minRows={3}
            value={formData.comment}
            onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
          />

          {/* Кнопки */}
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" loading={loading}>
              {isEditing ? 'Сохранить' : 'Создать'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default MasterClassFormModal;
